import React, { useEffect, useRef } from 'react';
import { YOLO_MAX, type Pt } from '../../utils/projection';
import { drawWarpedQuad } from './meshWarp';

export interface OverlayRect {
  // in perspective-rect space (0..pw, 0..ph)
  x: number; y: number; w: number; h: number; label?: string;
}

interface CropPreviewProps {
  imageUrl: string;
  /** 4 normalized points, TL,TR,BR,BL. */
  pointsNorm: Pt[];
  perspectiveWidth: number;
  perspectiveHeight: number;
  /** true => mirror YOLO_SHAPE letterbox-to-square (rack/shelf); false => raw (table). */
  letterbox: boolean;
  overlays?: OverlayRect[];
  boxSize?: number; // preview canvas px, default 240
}

const CropPreview: React.FC<CropPreviewProps> = ({
  imageUrl, pointsNorm, perspectiveWidth, perspectiveHeight, letterbox, overlays, boxSize = 240,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pw = perspectiveWidth, ph = perspectiveHeight;
    canvas.width = boxSize; canvas.height = boxSize;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, boxSize, boxSize);
    // black backdrop = the letterbox bars / table background
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, boxSize, boxSize);
    if (!pw || !ph || pointsNorm.length !== 4) return;

    const img = new window.Image();
    // No crossOrigin: the frame server doesn't send CORS headers, and setting it
    // makes the load fail (onerror) → blank preview. We only drawImage (never read
    // pixels back via getImageData/toDataURL), so a tainted canvas is harmless.
    img.onload = () => {
      // src quad in image px
      const srcPx: Pt[] = pointsNorm.map(([x, y]) => [x * img.width, y * img.height]);
      // Where pw x ph lands inside boxSize:
      let drawW: number, drawH: number;
      if (letterbox) {
        const r = YOLO_MAX / Math.max(pw, ph);
        const sw = pw * r, sh = ph * r; // post-resize dims (longer side = 640)
        const fit = boxSize / YOLO_MAX;
        drawW = sw * fit; drawH = sh * fit;
      } else {
        const fit = boxSize / Math.max(pw, ph);
        drawW = pw * fit; drawH = ph * fit;
      }
      const offX = (boxSize - drawW) / 2, offY = (boxSize - drawH) / 2;

      drawWarpedQuad(ctx, img, srcPx, pw, ph, drawW, drawH, offX, offY);
      const toDst = (rx: number, ry: number): Pt => [offX + (rx / pw) * drawW, offY + (ry / ph) * drawH];

      // overlays (rect space -> dst)
      if (overlays && overlays.length) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.strokeStyle = 'rgba(34,197,94,0.9)';
        ctx.lineWidth = 1.5;
        for (const o of overlays) {
          const a = toDst(o.x, o.y), b = toDst(o.x + o.w, o.y + o.h);
          ctx.strokeRect(a[0], a[1], b[0] - a[0], b[1] - a[1]);
        }
      }
    };
    img.onerror = () => {
      console.warn('CropPreview: frame image failed to load', imageUrl);
    };
    img.src = imageUrl;
  }, [imageUrl, pointsNorm, perspectiveWidth, perspectiveHeight, letterbox, overlays, boxSize]);

  return <canvas ref={canvasRef} className="rounded border border-border bg-black" />;
};

export default CropPreview;
